'use client'

import { useTranslations, useLocale } from 'next-intl'
import Image from 'next/image'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { BodyText, PageHeading } from '@/components/ui/typography'
import { Link } from '@/i18n/routing'
import { IconArrowLeft, IconCalendar } from '@tabler/icons-react'
import { useTagTranslations } from '@/hooks/use-tag-translations'
import { useBlogScript } from '@/components/blog-script-provider'
import { ScriptSwitch } from '@/components/script-switch'

interface BlogPostHeaderProps {
  title: string
  description: string
  publishedAt: string
  updatedAt?: string
  author?: string
  reviewedBy?: string
  tags: string[]
  location?: 'china' | 'taiwan' | 'both'
  heroImage?: string
}

export function BlogPostHeader({
  title,
  description,
  publishedAt,
  updatedAt,
  author,
  reviewedBy,
  tags,
  location,
  heroImage
}: BlogPostHeaderProps) {
  const t = useTranslations('blog')
  const locale = useLocale()
  const { translateBlogTag } = useTagTranslations()
  const { script, toggleScript } = useBlogScript()
  const [imageError, setImageError] = useState(false)

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  return (
    <>
      {/* Back button */}
      <Button asChild variant="ghost" className="-ml-2 w-fit">
        <Link href="/blog">
          <IconArrowLeft className="mr-2 h-4 w-4" />
          {t('backToBlogs')}
        </Link>
      </Button>

      {/* Hero image - only show if image exists and loads successfully */}
      {heroImage && !imageError && (
        <div className="relative aspect-2/1 w-full overflow-hidden rounded-2xl">
          <Image
            src={heroImage}
            alt={title}
            fill
            className="object-cover"
            priority
            sizes="(max-width: 768px) 100vw, 896px"
            onError={() => setImageError(true)}
          />
        </div>
      )}

      {/* Blog post header */}
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <Badge key={tag} variant="outline" size="sm">
              {translateBlogTag(tag)}
            </Badge>
          ))}
          {location && (
            <Badge key="location" variant="secondary" size="sm">
              {t(`location.${location}`)}
            </Badge>
          )}
        </div>
        <PageHeading>{title}</PageHeading>
        <BodyText className="text-muted-foreground text-lg">
          {description}
        </BodyText>
        <div className="text-muted-foreground flex flex-wrap items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <IconCalendar className="h-4 w-4" />
            {updatedAt && updatedAt !== publishedAt
              ? `Updated ${formatDate(updatedAt)}`
              : `Published ${formatDate(publishedAt)}`}
          </div>
          {author && (
            <div className="flex items-center gap-2">
              <span className="font-medium">By</span>
              <span>{author}</span>
            </div>
          )}
          {reviewedBy && (
            <div className="flex items-center gap-2">
              <span className="font-medium">Reviewed by</span>
              <span>{reviewedBy}</span>
            </div>
          )}
          <ScriptSwitch script={script} onToggle={toggleScript} />
        </div>
      </div>
    </>
  )
}
